import type { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { JSX } from 'react';
import { getDotPattern } from './utils/getDotPattern.ts';

type HandleBackgroundType = (color: string, theme: Theme) => string | undefined;

const handleBackgroundType: HandleBackgroundType = (color, theme) => {
  switch (color) {
    // Green statuses (success states)
    case 'active':
    case 'activated':
    case 'allowed':
    case 'exported':
    case 'reviewed':
    case 'running':
    case 'ready':
    case 'live':
    case 'accepted':
    case 'synced':
    case 'completed': // Green dot for the last dot in 3-dot pattern
      return theme.palette.success.main;

    // Red statuses (error states)
    case 'banned':
    case 'error':
    case 'stopped':
    case 'activate-failed':
    case 'expire-failed':
    case 'export-failed':
    case 'canceled':
    case 'rejected':
    case 'failed-to-sync':
    case 'deal-expired': // Red dot for the last dot in 3-dot pattern
      return theme.palette.error.main;

    case 'publish-failed':
      return theme.palette.charts.tomato;

    // Blue statuses (processing/in-progress states)
    case 'exporting':
    case 'goalreached':
    case 'publishing':
    case 'initializing':
    case 'proposed': // Blue dots for 2-dot pattern
    case 'live-not-spending': // Blue dot for 1st dot in 3-dot pattern
    case 'not-started': // Blue dot for 1st dot in 3-dot pattern
      return theme.palette.primary.main;

    // Gray statuses (inactive/neutral states)
    case 'expired':
    case 'ignored':
    case 'inactive':
    case 'notallowed':
    case 'purged':
    case 'pending-acceptance': // All 3 gray dots
    case 'sync-deleted':
      return theme.palette.background.boxDarkest;

    // Orange statuses (warning states)
    case 'draft':
    case 'new':
    case 'paused':
    case 'pending':
    case 'warning':
    case 'expiring':
      return theme.palette.warning.main;

    case 'processing':
      return theme.palette.primary.main;

    default:
      return undefined;
  }
};

type StyledSpanProps = {
  color: string;
  'data-test'?: string;
  sx?: SxProps;
};

const StyledSpan = (props: StyledSpanProps) => (
  <Typography
    data-color={props.color}
    data-test={props['data-test']}
    variant="caption"
    sx={{
      '&:nth-last-of-type(2)': {
        marginRight: 1,
      },
      backgroundColor: theme => handleBackgroundType(props.color, theme),
      borderRadius: '100%',
      display: 'inline-block',
      height: 8,
      marginRight: 0.25,
      width: 8,
      ...(props?.sx ?? {}),
    }}
  />
);

export type IndicatorStatus =
  // Green statuses (success states)
  | 'active'
  | 'activated'
  | 'allowed'
  | 'accepted'
  | 'completed'
  | 'exported'
  | 'live'
  | 'ready'
  | 'reviewed'
  | 'running'
  | 'synced'
  // Red statuses (error states)
  | 'activate-failed'
  | 'banned'
  | 'canceled'
  | 'deal-expired'
  | 'error'
  | 'expire-failed'
  | 'export-failed'
  | 'failed-to-sync'
  | 'rejected'
  | 'stopped'
  // Special red status (tomato color)
  | 'publish-failed'
  // Orange statuses (warning states)
  | 'draft'
  | 'expiring'
  | 'new'
  | 'paused'
  | 'pending'
  | 'warning'
  // Blue statuses (processing/in-progress states)
  | 'exporting'
  | 'goalreached'
  | 'initializing'
  | 'live-not-spending'
  | 'not-started'
  | 'processing'
  | 'proposed'
  | 'publishing'
  // Gray statuses (inactive/neutral states)
  | 'expired'
  | 'ignored'
  | 'inactive'
  | 'notallowed'
  | 'pending-acceptance'
  | 'purged'
  | 'sync-deleted';

export type StatusIndicatorProps = {
  status?: string;
  label?: string;
  className?: string;
  'data-test'?: string;
  tooltip?: string;
  iconStyles?: SxProps;
  textStyles?: SxProps;
  wrapperStyles?: SxProps;
};

export const StatusIndicator = (props: StatusIndicatorProps): JSX.Element => {
  const { status = 'inactive', className, label, tooltip, iconStyles, textStyles, wrapperStyles } = props;
  const statusKebabLowerCase = status?.toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
  const dotPattern = getDotPattern(statusKebabLowerCase);

  return (
    <Tooltip title={tooltip || ''} disableHoverListener={!tooltip} placement="top">
      <Typography component="span" className={className} data-test={props['data-test']} sx={wrapperStyles}>
        {dotPattern.map((dotColor, index) => (
          <StyledSpan
            key={index}
            data-test={
              dotPattern.length === 1 ? `status-${statusKebabLowerCase}` : `status-${statusKebabLowerCase}-dot-${index}`
            }
            color={dotColor}
            sx={iconStyles}
          />
        ))}
        <Typography variant="overline" color="textSecondary" sx={{ wordBreak: 'break-all', ...(textStyles ?? {}) }}>
          {label?.toUpperCase() || status?.replace(/[-_]/g, ' ').toUpperCase()}
        </Typography>
      </Typography>
    </Tooltip>
  );
};
